import React, { useState, useEffect, useMemo } from 'react'
import { FaSearch, FaCheckCircle, FaEdit, FaTrash } from 'react-icons/fa'
import { useParams } from 'react-router'
import { Heading, Box, Tooltip, ResponsiveStack, Stack, Button, FormControl, DateChooser, InputGroup, Input, Icon, Textarea, Select, Alert, SimpleTable, Badge, EmptyBox, ConfirmButton, SimpleModal, SimpleBreadcrumbs, UserChip, UserChooser, Card } from '@/components/lib'
import api from '../../../api'
import utils from '../../../utils/utils'
import useStore from '../../../store'
import useTitle from '../../../hooks/useTitle'

export const CATEGORIES = [
  { key: 'mv', name: 'MV' },
  { key: 'ssv', name: 'SSV' }
]
export const PRIORITIES = [
  { key: 'low', name: 'Low', color: 'green' },
  { key: 'medium', name: 'Medium', color: 'orange' },
  { key: 'high', name: 'High', color: 'red' }
]
export const STATUSES = [
  { key: 'pending', name: 'Pending', color: 'purple' },
  { key: 'completed', name: 'Completed', color: 'green' }
]
export const ISSUE_CATEGORIES = [
  { key: 'informedConsent', name: 'Informed consent' },
  { key: 'eSource', name: 'eSource' },
  { key: 'ePro', name: 'ePro' }
]
export const ISSUE_SEVERITIES = [
  { key: 'minor', name: 'Minor', color: 'yellow' },
  { key: 'major', name: 'Major', color: 'orange' },
  { key: 'critical', name: 'Critical', color: 'red' }
]
export const ISSUE_STATUSES = [
  { key: 'unresolved', name: 'Unresolved', color: 'orange' },
  { key: 'resolved', name: 'Resolved', color: 'green' }
]

export default function SiteMonitoring () {
  const [site, setSite] = useState()
  const [monitoring, setMonitoring] = useState([])
  const [filter, setFilter] = useState('')
  const [filterCategory, setFilterCategory] = useState('')
  const [filterPriority, setFilterPriority] = useState('')
  const { siteId } = useParams()
  const { trial, user } = useStore()
  useTitle('Site Monitoring', trial)

  useEffect(() => {
    if (!siteId) return
    api.sites.get(siteId, d => {
      setSite(d)
    })
    api.siteMonitoring.getForSite(siteId, d => {
      setMonitoring(d.siteMonitoring)
    })
  }, [siteId])

  function addMonitoring (monitor) {
    const newMonitoring = [...monitoring]
    if (newMonitoring.find(m => m._id === monitor._id)) {
      newMonitoring[newMonitoring.findIndex(m => m._id === monitor._id)] = monitor
    } else {
      newMonitoring.push(monitor)
    }
    setMonitoring(newMonitoring)
  }

  function deleteMonitoring (id) {
    api.siteMonitoring.delete(id, d => {
      setMonitoring(monitoring.filter(m => m._id !== id))
      utils.toast('success', 'Monitoring activity deleted')
    }, err => {
      utils.toast('error', 'Error deleting monitoring activity', err.message)
    })
  }

  let filteredMonitoring = filter ? monitoring.filter(m => m.id?.toLowerCase()?.includes(filter) || m.category?.includes(filter)) : monitoring
  if (filterCategory) filteredMonitoring = filteredMonitoring.filter(m => m.category === filterCategory)
  if (filterPriority) filteredMonitoring = filteredMonitoring.filter(m => m.priority === filterPriority)

  return (
    <Stack gap={5}>
      <SimpleBreadcrumbs items={[
        { label: 'Sites', to: `/trials/${trial._id}/sites` },
        { label: site?.name },
        { label: 'Monitoring' }
      ]}
      />
      <Box display='flex' justifyContent='space-between'>
        <Heading as='h2' size='lg'>Monitoring at site {site?.name}</Heading>
        <MonitoringEditor
          siteId={siteId}
          trigger={<Button colorPalette='primary'>Add monitoring activity</Button>}
          onChange={addMonitoring}
        />
      </Box>

      {(monitoring?.length > 0)
        ? (
          <>
            <Stack direction='row'>
              <InputGroup w={200} leftIcon={<Icon as={FaSearch} color='fg.muted' />}>
                <Input placeholder='Type to search...' value={filter} onChange={e => setFilter(e.target.value.toLowerCase())} />
              </InputGroup>
              <Select w={150} value={filterCategory} onChange={e => setFilterCategory(e.target.value)}>
                <option value=''>All categories</option>
                {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.name}</option>)}
              </Select>
              <Tooltip label='Filter by priority' placement='top'>
                <Select w={150} value={filterPriority} onChange={e => setFilterPriority(e.target.value)}>
                  <option value=''>All priorities</option>
                  {PRIORITIES.map(p => <option key={p.key} value={p.key}>{p.name}</option>)}
                </Select>
              </Tooltip>
            </Stack>

            <SimpleTable
              tableProps={{ size: 'sm', variant: 'simple' }}
              headers={['ID', 'Created by', 'Assignees', 'Category', 'Priority', 'Scheduled', 'Duration', 'Status', 'Issues', '']}
              rows={filteredMonitoring.map(monitor => {
                const priority = PRIORITIES.find(p => p.key === monitor.priority)
                const category = CATEGORIES.find(c => c.key === monitor.category)
                const status = STATUSES.find(s => s.key === monitor.status)
                return [
                  monitor.id,
                  <UserChip key={monitor._id} compact user={monitor.createdByUser} />,
                  <Stack key={monitor._id} direction='row' spacing={0}>
                    {monitor.assigneeUsers?.length === 0 && <Badge colorPalette='orange'>Unassigned</Badge>}
                    {monitor.assigneeUsers?.map(u => <UserChip key={u._id} user={u} onlyAvatar compact />)}
                  </Stack>,
                  <Badge key={monitor._id}>{category?.name}</Badge>,
                  <Badge key={monitor._id} colorPalette={priority?.color}>{priority?.name}</Badge>,
                  monitor.scheduledAt ? utils.formatDate(monitor.scheduledAt) : '',
                  monitor.duration ? `${monitor.duration} days` : '',
                  <Badge key={monitor._id} colorPalette={status?.color}>{status?.name}</Badge>,
                  monitor.issueCount,
                  <Stack key={monitor._id} direction='row' justifyContent='end'>
                    <MonitoringEditor
                      siteId={siteId}
                      monitor={monitor}
                      trigger={<Button size='sm' colorPalette='primary' variant='outline'>Manage</Button>}
                      onChange={addMonitoring}
                    />
                    {utils.canManageTrial(user, trial) &&
                      <ConfirmButton
                        trigger={<Button size='sm' variant='ghost'><Icon as={FaTrash} /></Button>}
                        header='Really delete this monitoring activity?'
                        footer='This cannot be undone.'
                        onConfirm={e => deleteMonitoring(monitor._id)}
                      />}
                  </Stack>
                ]
              })}
            />
          </>)
        : (
          <EmptyBox
            icon='checklist' title='Create a monitoring activity for this site' description='Capture details or issues about recent monitoring activities made as part of trial preparation or for audit purposes.'
          >
            <MonitoringEditor
              siteId={siteId}
              trigger={<Button colorPalette='primary'>Add a monitoring activity</Button>}
              onChange={addMonitoring}
            />
          </EmptyBox>)}
    </Stack>
  )
}

function MonitoringEditor ({ trigger, siteId, monitor, onChange }) {
  const [isOpen, setIsOpen] = useState(false)
  const [id, setId] = useState(monitor?.id)
  const [category, setCategory] = useState(monitor?.category || 'mv')
  const [priority, setPriority] = useState(monitor?.priority || 'low')
  const [status, setStatus] = useState(monitor?.status || 'pending')
  const [duration, setDuration] = useState(monitor?.duration || 1)
  const [scheduledAt, setScheduledAt] = useState(monitor?.scheduledAt)
  const [completedAt, setCompletedAt] = useState(monitor?.completedAt)
  const [assignees, setAssignees] = useState(monitor?.assignees || [])
  const [notes, setNotes] = useState(monitor?.notes)
  const [issues, setIssues] = useState([])
  const { trial, getTrialUsers } = useStore()
  const trialId = trial?._id
  const trialUsers = useMemo(() => getTrialUsers(trialId), [trialId])

  useEffect(() => {
    if (monitor && isOpen) {
      api.siteMonitoringIssues.getForMonitoring(monitor._id, d => {
        setIssues(d.siteMonitoringIssues)
      })
    } else {
      setIssues([])
    }
  }, [monitor, isOpen])

  function save () {
    const data = { id, category, priority, notes, status, duration, scheduledAt, completedAt, assignees }
    if (monitor) {
      api.siteMonitoring.update(monitor._id, data, d => {
        onChange(d)
        setIsOpen(false)
      }, err => {
        utils.toast('error', 'Error saving monitoring activity', err.message)
      })
      return
    }
    api.siteMonitoring.create(siteId, data, d => {
      onChange(d)
      setIsOpen(false)
    }, err => {
      utils.toast('error', 'Error saving monitoring activity', err.message)
    })
  }

  function addIssue (issue) {
    if (issue.delete) {
      setIssues(issues.filter(i => i._id !== issue.delete))
      return
    }
    const newIssues = [...issues]
    if (newIssues.find(i => i._id === issue._id)) {
      newIssues[newIssues.findIndex(i => i._id === issue._id)] = issue
    } else {
      newIssues.push(issue)
    }
    setIssues(newIssues)
  }

  return (
    <>
      {React.cloneElement(trigger, { onClick: () => setIsOpen(true) })}
      <SimpleModal
        isOpen={isOpen} onClose={() => setIsOpen(false)}
        size='4xl'
        header={monitor ? 'Edit monitoring activity' : 'Add monitoring activity'}
        body={
          <ResponsiveStack>
            <Card gap={3} w='300px'>
              <Heading size='sm'>Monitoring details</Heading>
              <FormControl label='Monitoring ID'>
                <Input size='sm' placeholder='MV-001' type='text' value={id || ''} onChange={e => setId(e.target.value)} />
              </FormControl>
              <FormControl label='Category'>
                <Select size='sm' value={category || ''} onChange={e => setCategory(e.target.value)}>
                  {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.name}</option>)}
                </Select>
              </FormControl>
              <FormControl label='Priority'>
                <Select size='sm' value={priority || ''} onChange={e => setPriority(e.target.value)}>
                  {PRIORITIES.map(p => <option key={p.key} value={p.key}>{p.name}</option>)}
                </Select>
              </FormControl>
              <FormControl label='Status'>
                <Select size='sm' value={status || ''} onChange={e => setStatus(e.target.value)}>
                  {STATUSES.map(s => <option key={s.key} value={s.key}>{s.name}</option>)}
                </Select>
              </FormControl>
              <FormControl label='Scheduled date'>
                <DateChooser
                  size='sm' value={scheduledAt} onChange={(date) => {
                    setScheduledAt(date ? date.toISOString() : '')
                  }}
                />
              </FormControl>
              <FormControl label='Duration (days)'>
                <Input size='sm' placeholder='1' type='number' value={duration || ''} onChange={e => setDuration(e.target.value)} />
              </FormControl>
              <FormControl label='Completed date'>
                <DateChooser
                  size='sm' value={completedAt} onChange={(date) => {
                    setCompletedAt(date ? date.toISOString() : '')
                  }}
                />
              </FormControl>

              <FormControl label='Assignees'>
                <UserChooser
                  size='sm' placeholder='Tap to assign...' users={trialUsers.filter(u => !assignees.includes(u._id))} onChosen={userIds => {
                    if (assignees.includes(userIds[0])) return
                    setAssignees([...assignees, userIds[0]])
                  }}
                />
                <Stack>
                  {assignees.map(a => {
                    const assigneeUser = trialUsers.find(u => u._id === a)
                    return (
                      <Stack direction='row' key={a}>
                        <Box flex={1}>
                          <UserChip user={assigneeUser} compact />
                        </Box>
                        <Button size='xs' onClick={() => setAssignees(assignees.filter(u => u !== a))} variant='ghost'><Icon as={FaTrash} /></Button>
                      </Stack>
                    )
                  })}
                </Stack>
              </FormControl>
            </Card>

            <Stack gap={5} p={3} borderRadius='md' flex={1}>
              <FormControl label='Notes'>
                <Textarea placeholder='Write notes about this monitoring activity...' value={notes || ''} onChange={e => setNotes(e.target.value)} rows={10} />
              </FormControl>

              <Heading size='sm'>Issues</Heading>
              {issues.length === 0
                ? (
                  <EmptyBox
                    icon='wip' title='No issues raised' description='Raise issues identified with this monitoring activity.'
                  >
                    {monitor
                      ? <IssueEditor
                          monitoringId={monitor?._id} onChange={addIssue}
                          trigger={<Button size='sm' colorPalette='blue' variant='outline'>Raise an issue</Button>}
                        />
                      : <Alert variant='left-accent'>Issues can only be raised after the monitoring activity has been created.</Alert>}
                  </EmptyBox>)
                : (
                  <>
                    <SimpleTable
                      asCard
                      size='sm'
                      headers={['Name', 'Raised by', 'Assignees', 'Category', 'GCP NC', 'Protocol NC', 'Severity', 'Status', '']}
                      rows={issues.map(i => {
                        const severity = ISSUE_SEVERITIES.find(s => s.key === i.severity)
                        const status = ISSUE_STATUSES.find(s => s.key === i.status)
                        const category = ISSUE_CATEGORIES.find(c => c.key === i.category)
                        return [
                          i.name,
                          <UserChip key={i._id} user={i.createdByUser} onlyAvatar compact />,
                          <Stack key={i._id} direction='row' spacing={0}>
                            {i.assigneeUsers.map(u => <UserChip key={u._id} user={u} onlyAvatar compact />)}
                          </Stack>,
                          <Badge key={i._id}>{category?.name}</Badge>,
                          i.gcpNonCompliance ? <Icon key={i._id} as={FaCheckCircle} color='red' /> : '',
                          i.protocolNonCompliance ? <Icon key={i._id} as={FaCheckCircle} color='red' /> : '',
                          <Badge key={i._id} colorPalette={severity?.color}>{severity?.name}</Badge>,
                          <Badge key={i._id} colorPalette={status?.color}>{status?.name}</Badge>,
                          <IssueEditor
                            key={i._id}
                            issue={i} monitoringId={monitor?._id} onChange={addIssue}
                            trigger={<Button size='sm' colorPalette='blue' variant='ghost'><Icon as={FaEdit} /></Button>}
                          />
                        ]
                      })}
                    />

                    <Box display='flex' justifyContent='end'>
                      <IssueEditor
                        monitoringId={monitor._id} onChange={addIssue}
                        trigger={<Button size='sm' colorPalette='blue' variant='outline'>Raise an issue</Button>}
                      />
                    </Box>
                  </>)}
            </Stack>
          </ResponsiveStack>
        }
        footer={
          <Stack justifyContent='end' direction='row'>
            <Button onClick={() => setIsOpen(false)}>Cancel</Button>
            <Button colorPalette='primary' onClick={save}>Save</Button>
          </Stack>
        }
      />
    </>
  )
}

function IssueEditor ({ trigger, monitoringId, issue, onChange }) {
  const [isOpen, setIsOpen] = useState(false)
  const [name, setName] = useState(issue?.name)
  const [category, setCategory] = useState(issue?.category || 'informedConsent')
  const [gcpNonCompliance, setGcpNonCompliance] = useState(issue?.gcpNc || false)
  const [protocolNonCompliance, setProtocolNonCompliance] = useState(issue?.protocolNc || false)
  const [severity, setSeverity] = useState(issue?.severity || 'minor')
  const [status, setStatus] = useState(issue?.status || 'unresolved')
  const [assignees, setAssignees] = useState(issue?.assignees || [])
  const { trial, getTrialUsers } = useStore()
  const trialId = trial?._id
  const trialUsers = useMemo(() => getTrialUsers(trialId), [trialId])

  if (!monitoringId) return null

  function save () {
    const data = { name, category, gcpNonCompliance, protocolNonCompliance, severity, status, assignees }
    if (issue) {
      api.siteMonitoringIssues.update(issue._id, data, d => {
        onChange(d)
        setIsOpen(false)
      }, err => {
        utils.toast('error', 'Error saving issue', err.message)
      })
      return
    }
    api.siteMonitoringIssues.create(monitoringId, data, d => {
      onChange(d)
      setIsOpen(false)
    }, err => {
      utils.toast('error', 'Error saving issue', err.message)
    })
  }

  function deleteIssue () {
    api.siteMonitoringIssues.delete(issue._id, d => {
      onChange({ delete: issue._id })
      setIsOpen(false)
    }, err => {
      utils.toast('error', 'Error deleting issue', err.message)
    })
  }

  return (
    <>
      <SimpleModal
        contentProps={{ width: '500px' }}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        trigger={React.cloneElement(trigger, { onClick: () => setIsOpen(true) })}
        header={<Heading size='sm'>{issue ? 'Edit issue' : 'Add an issue'}</Heading>}
        body={
          <Stack gap={3}>
            <Card variant='elevated' gap={3}>
              <Box>
                <FormControl label='Name or description'>
                  <Input size='sm' placeholder='Issue name' type='text' value={name || ''} onChange={e => setName(e.target.value)} />
                </FormControl>
                <FormControl label='Category'>
                  <Select size='sm' value={category || ''} onChange={e => setCategory(e.target.value)}>
                    {ISSUE_CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.name}</option>)}
                  </Select>
                </FormControl>
                <FormControl label='Status'>
                  <Select size='sm' value={status || ''} onChange={e => setStatus(e.target.value)}>
                    {ISSUE_STATUSES.map(s => <option key={s.key} value={s.key}>{s.name}</option>)}
                  </Select>
                </FormControl>
              </Box>
              <Box>
                <FormControl label='GCP non-compliance'>
                  <Select size='sm' value={gcpNonCompliance || ''} onChange={e => setGcpNonCompliance(e.target.value)}>
                    <option value='false'>No</option>
                    <option value='true'>Yes</option>
                  </Select>
                </FormControl>
                <FormControl label='Protocol non-compliance'>
                  <Select size='sm' value={protocolNonCompliance || ''} onChange={e => setProtocolNonCompliance(e.target.value)}>
                    <option value='false'>No</option>
                    <option value='true'>Yes</option>
                  </Select>
                </FormControl>
                <FormControl label='Severity'>
                  <Select size='sm' value={severity || ''} onChange={e => setSeverity(e.target.value)}>
                    {ISSUE_SEVERITIES.map(s => <option key={s.key} value={s.key}>{s.name}</option>)}
                  </Select>
                </FormControl>
              </Box>
            </Card>

            <Card variant='elevated' gap={3}>
              <Heading size='sm'>Assignees</Heading>
              <Stack direction='row' spacing={3}>
                <UserChooser
                  size='sm' users={trialUsers.filter(u => !assignees.includes(u._id))} onChosen={userIds => {
                    if (assignees.includes(userIds[0])) return
                    setAssignees([...assignees, userIds[0]])
                  }} placeholder='Tap to assign...'
                />
                <Stack>
                  {assignees.map(a => {
                    const assigneeUser = trialUsers.find(u => u._id === a)
                    return (
                      <Stack direction='row' key={a}>
                        <Box flex={1}>
                          <UserChip user={assigneeUser} compact />
                        </Box>
                        <Button size='xs' onClick={() => setAssignees(assignees.filter(u => u !== a))} variant='ghost'><Icon as={FaTrash} /></Button>
                      </Stack>
                    )
                  })}
                </Stack>
              </Stack>
            </Card>

            <Stack direction='row' justifyContent='end'>
              {issue &&
                <ConfirmButton
                  trigger={<Button size='sm' variant='ghost'><Icon><FaTrash /></Icon></Button>}
                  header='Really delete this issue?'
                  footer='This cannot be undone.'
                  onConfirm={deleteIssue}
                />}
              <Button size='sm' onClick={() => setIsOpen(false)}>Cancel</Button>
              <Button size='sm' colorPalette='primary' onClick={save}>Save issue</Button>
            </Stack>
          </Stack>
        }
      />
    </>
  )
}
